import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import { Redirect } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { get } from 'lodash';
import moment from 'moment';

import { applicationSelectors, userSelectors } from '../../redux/selectors';
import { useRoutes, useIsMobileLayout } from '../../hooks';
import {
  sectionsAPICurrentSection,
  sectionsAPINextSection,
  sectionsAPIPreviousSection,
} from '../../services/apis/respondents/sections.respondents.api';
import {
  showEnrollment,
  defaultParams,
  finishEnrollment,
  updateEnrollment,
} from '../../services/apis/respondents/enrollments.respondents.api';
import { applicationActions } from '../../redux/actions';

import Tooltip from '../../components/Tooltips/Tooltip';
import IconButton from '../../components/Buttons/IconButton';
import Box from '../../components/Styled/Box';
import ProgressBar from '../../components/ProgressBar';

const Questionnaire = React.lazy(() => import('../../containers/Questionnaire/Respondent/Questionnaire.respondent'));
const QuestionnaireDescription = React.lazy(() => import('../../containers/Questionnaire/Respondent/QuestionnaireDescription.respondent'));
const QuestionnaireCover = React.lazy(() => import('../../containers/Questionnaire/Respondent/QuestionnaireCover.respondent'));

const QuestionnairePage = ({
  match,
  history,
  enrollmentId: enrollmentIdProps,
  applicationQuestionnaireToken,
  isPublic,
}) => {
  const { formatMessage: f } = useIntl();
  const dispatch = useDispatch();
  const routes = useRoutes();
  const isMobile = useIsMobileLayout();
  const isManagerView = useSelector(applicationSelectors.isManagerView);
  const user_name = useSelector(userSelectors.username);

  const enrollmentId = enrollmentIdProps || match.params.id;

  const [shouldRedirectToHome, setShouldRedirectToHome] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [sectionData, setSectionData] = useState(null);
  const [showDescription, setShowDescription] = useState(true);
  const [showCover, setShowCover] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [percentage, setPercentage] = useState(0);
  const timeRef = React.useRef(0);

  // traduz as variáveis vindas do rails admin para substituir nos questionários
  const customVariables = React.useMemo(() => {
    const endDate = get(enrollmentData, 'application_questionnaire.end_date', null);
    const questionnaire = get(enrollmentData, 'application_questionnaire.questionnaire', {});
    return {
      company_name: get(enrollmentData, 'application_questionnaire.application.company.name', ''),
      user_name,
      assessed_user_name: get(enrollmentData, 'assessed_user.name', ''),
      end_date: endDate ? moment(endDate).format('DD MMMM') : '',
      application_title: get(enrollmentData, 'application_questionnaire.questionnaire.title', ''),
      role: questionnaire.role_name || questionnaire.role || '',
    };
  }, [enrollmentData, user_name]);

  const shouldShowProgress = React.useMemo(
    () => (!isManagerView || isPublic),
    [isManagerView, isPublic],
  );

  const isLastSection = React.useMemo(() => (
    sectionData
    && sectionData.last
    && sectionData.last_page
  ), [sectionData]);

  const timerDisabled = React.useMemo(() => {
    const status = get(enrollmentData, 'status', '');
    return ['answered', 'results_available'].includes(status);
  },
  [enrollmentData]);

  const updateQuestionAnswer = useCallback((answer) => {
    setEnrollmentData((_data) => {
      const answerExists = Boolean(_data.answers.find((d) => d.id === answer.id));

      return {
        ..._data,
        answers: answerExists
          ? _data.answers.map((d) => (d.id === answer.id ? answer : d))
          : [..._data.answers, answer],
      };
    });
  }, []);

  const hideDescription = useCallback(() => setShowDescription(false), []);
  const hideCover = useCallback(() => setShowCover(false), []);

  const onUpdateEnrollment = useCallback(async () => {
    await updateEnrollment({
      id: enrollmentId,
      applicationQuestionnaireToken,
      enrollment: { spent_time: timeRef.current },
      apiArgs: {
        apiOptions: { options: { disableHeaders: isPublic } },
      },
    });
  }, [applicationQuestionnaireToken, enrollmentId, isPublic]);

  const fetchQuestionnaireData = useCallback(async () => {
    const { data } = await showEnrollment({
      id: enrollmentId,
      params: applicationQuestionnaireToken ? defaultParams.show_public : defaultParams.show,
      applicationQuestionnaireToken,
      apiArgs: {
        options: { toggleLoading: false },
        apiOptions: { options: { disableHeaders: isPublic } },
      },
    });

    if (data.pages_completion_ratio) {
      const apiPercentage = data.pages_completion_ratio * 100;
      setPercentage(apiPercentage.toFixed(0));
    }

    if (data.spent_time) {
      timeRef.current = data.spent_time;
    }

    setEnrollmentData({ ...data, id: data.hashid || data.id });
    setShowDescription(data.status === 'ready_to_start' || data.status === null);
    setShowCover(data.application_questionnaire.application.avatar_url);
  }, [applicationQuestionnaireToken, enrollmentId, isPublic]);

  const fetchQuestionnaireSections = useCallback(async ({ callback, params, formik } = {}) => {
    setLoading(true);
    if (formik) { formik.setSubmitting(true); }

    try {
      const { data: responseData } = await callback({
        enrollment_id: enrollmentId,
        ...applicationQuestionnaireToken && { applicationQuestionnaireToken },
        ...typeof params === 'string' && { params },
        apiArgs: {
          options: { toggleLoading: false },
          apiOptions: { options: { disableHeaders: isPublic } },
        },
      });

      const { questionnaire, ..._data } = responseData;

      if (_data.pages_completion_ratio) {
        const apiPercentage = _data.pages_completion_ratio * 100;
        setPercentage(apiPercentage.toFixed(0));
      }

      if (questionnaire) {
        setEnrollmentData(questionnaire);
      }

      window.scrollTo(0, 0);
      setSectionData(_data);
    }

    finally {
      setLoading(false);
      if (formik) { formik.setSubmitting(false); }
    }
  },
  [enrollmentId, applicationQuestionnaireToken, isPublic]);

  const fetchCurrentSection = useCallback((params) => (
    fetchQuestionnaireSections({ callback: sectionsAPICurrentSection, params })
  ), [fetchQuestionnaireSections]);

  const fetchNextSection = useCallback(({ params = '', formik } = { params: '' }) => {
    fetchQuestionnaireSections({
      callback: sectionsAPINextSection,
      params,
      formik,
    });
  }, [fetchQuestionnaireSections]);

  const fetchPreviousSection = useCallback(({ params = '', formik } = { params: '' }) => {
    if (sectionData.position === 1) {
      setShowDescription(true);
    }

    else {
      fetchQuestionnaireSections({
        callback: sectionsAPIPreviousSection,
        params,
        formik,
      });
    }
  }, [sectionData, fetchQuestionnaireSections]);

  const confirmationMessageDialog = useCallback(({ formik }) => {
    fetchQuestionnaireData();
    fetchCurrentSection();
    dispatch(applicationActions.toggleDialog({
      title: f({ id: 'pages.respondents.confirmation_message_dialog.title' }),
      body: f({ id: 'pages.respondents.confirmation_message_dialog.body' }),
      button: {
        text: f({ id: 'pages.respondents.confirmation_message_dialog.button_finish' }),
        onClick: async () => {
          await finishEnrollment({
            id: enrollmentId,
            applicationQuestionnaireToken,
            apiArgs: {
              apiOptions: { options: { disableHeaders: isPublic } },
            },
          });
          onUpdateEnrollment();
          setShouldRedirectToHome(true);
          dispatch(applicationActions.saveApplication({}));
        },
      },
      leftButton: {
        text: f({ id: 'pages.respondents.confirmation_message_dialog.button_check' }),
        onClick: formik.setSubmitting(false),
      },
    }));
  },
  [
    fetchQuestionnaireData,
    fetchCurrentSection,
    dispatch,
    f,
    enrollmentId,
    applicationQuestionnaireToken,
    isPublic,
    onUpdateEnrollment,
  ]);

  useEffect(() => {
    fetchQuestionnaireData();
    fetchCurrentSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sectionData && enrollmentData) {
      setInitialized(true);
    }
  }, [sectionData, enrollmentData]);

  useEffect(() => {
    const sendSpentTime = () => updateEnrollment({
      id: enrollmentId,
      applicationQuestionnaireToken,
      enrollment: {
        spent_time: timeRef.current,
      },
      apiArgs: {
        apiOptions: { options: { disableHeaders: isPublic } },
      },
    });

    const handleUnload = (e) => {
      sendSpentTime();
      e.preventDefault();
      const message = 'Você deseja sair sem salvar?';
      (e || window.event).returnValue = message;
    };

    const sendTime = () => {
      if (document.visibilityState === 'hidden') {
        sendSpentTime();
      }
    };

    window.addEventListener('beforeunload', handleUnload, true);
    document.addEventListener('visibilitychange', sendTime, true);

    return async () => {
      window.removeEventListener('beforeunload', handleUnload, true);
      document.removeEventListener('visibilitychange', sendTime, true);
    };
  }, [applicationQuestionnaireToken, enrollmentId, isPublic]);

  if (shouldRedirectToHome) {
    return (
      <Redirect
        to={routes.messages.thankYou}
      />
    );
  }

  return (
    <Box p={isMobile ? 0 : '106px 188px'} width='100%'>
      {loading && !sectionData && <Skeleton width='100%' height={800} />}

      {!isPublic && (
        <>
          {!isMobile && <Tooltip id='tooltip-home' />}

          <IconButton
            data-for='tooltip-home'
            data-tip={f({ id: 'pages.questionnaire.back_to_main_page' })}
            variant='secondary'
            position='fixed'
            icon='arrow-left'
            onClick={history.goBack}
            style={{ transform: 'translateX(-64px)' }}
          />
        </>
      )}

      {(initialized && showCover) && (
        <QuestionnaireCover
          enrollmentData={enrollmentData}
          isPublic={Boolean(applicationQuestionnaireToken)}
          onContinue={hideCover}
        />
      )}

      {(initialized && showDescription && !showCover) && (
        <QuestionnaireDescription
          enrollmentData={enrollmentData}
          onCancel={history.goBack}
          onContinue={hideDescription}
          isPublic={Boolean(applicationQuestionnaireToken)}
        />
      )}

      {(initialized && !showDescription && !showCover) && (
        <>
          {shouldShowProgress && (
            <ProgressBar
              percentage={percentage}
              initialTime={timeRef.current}
              timeRef={timeRef}
              timerDisabled={timerDisabled}
            />
          )}

          <Questionnaire
            isPublic={isPublic}
            isLastSection={isLastSection}
            enrollmentData={enrollmentData}
            sectionData={sectionData}
            fetchNextSection={isLastSection ? confirmationMessageDialog : fetchNextSection}
            fetchPreviousSection={fetchPreviousSection}
            updateQuestionAnswer={updateQuestionAnswer}
            applicationQuestionnaireToken={applicationQuestionnaireToken}
            onGoBack={onUpdateEnrollment}
            customVariables={customVariables}
          />
        </>
      )}
    </Box>
  );
};

QuestionnairePage.propTypes = {
  history: PropTypes.object.isRequired,
  enrollmentId: PropTypes.node,
  applicationQuestionnaireToken: PropTypes.string,
  match: PropTypes.shape({ params: PropTypes.object.isRequired }).isRequired,
  location: PropTypes.shape({ search: PropTypes.string.isRequired }),
  isPublic: PropTypes.bool,
};

QuestionnairePage.defaultProps = {
  enrollmentId: null,
  applicationQuestionnaireToken: null,
  location: { search: '' },
  isPublic: false,
};

export default QuestionnairePage;
